import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Home, Workflow, Users, BarChart3, Video, Music, Image,
  FileText, Podcast, HelpCircle, Shield,
  Megaphone, Target,
  BookOpen, LayoutDashboard, ClipboardList, Plug, Star,
  GraduationCap, Award, ChevronsLeft, ChevronsRight, Building2, Route, Sparkles,
  Presentation, UsersRound,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { useInstitutionAppTheme } from './InstitutionAppTheme';
import type { UserRole } from '@/types';
import { roleQualifiesForTrainerWebinarUi } from '@/lib/trainer-webinar-eligibility-ui';
import { roleQualifiesForEditorCreatorNav } from '@/lib/editor-role';
import { INSTITUTION_MENU_FLAGS, isInstitutionMenuEnabled } from '@/lib/institution-menu-flags';
import { SEGMENT_FEATURES } from '@/lib/institution-segment';
import { useInstitutionSegment } from '@/hooks/useInstitutionSegment';
import { rtlMirrorHorizontalChevron } from '@/lib/locale-direction';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** Si défini : masqué lorsque l’admin plateforme a désactivé l’entrée pour l’établissement. */
  institutionMenuFlag?: string;
  /** Fonctionnalité soumise au segment (éducation / entreprise), hors drapeau de menu. */
  segmentFeature?: string;
  requiresInstitution?: boolean;
}

interface NavSection {
  key: string;
  label: string;
  items: NavItem[];
}

const TEACHER_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [
      { label: 'nav.home', path: '/dashboard', icon: Home },
      { label: 'nav.learners', path: '/learners', icon: Users },
      {
        label: 'nav.supportGroups',
        path: '/groups',
        icon: UsersRound,
        requiresInstitution: true,
        institutionMenuFlag: INSTITUTION_MENU_FLAGS.tab_groups,
      },
      { label: 'nav.analytics', path: '/analytics', icon: BarChart3 },
      { label: 'nav.producerAssignments', path: '/producer-assignments', icon: Sparkles, requiresInstitution: true },
    ],
  },
  {
    key: 'creation',
    label: 'nav.section.creation',
    items: [
      { label: 'nav.eduflowList', path: '/eduflow/list', icon: Workflow },
      { label: 'nav.podcasts', path: '/podcasts', icon: Podcast },
      { label: 'nav.parcours', path: '/parcours', icon: Route },
    ],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [
      { label: 'nav.help', path: '/help', icon: HelpCircle },
    ],
  },
];
const TEACHER_NAV_ITEMS: NavItem[] = TEACHER_NAV_SECTIONS.flatMap((section) => section.items);

/** Espace éditeur : création + médias, sans apprenants, analytics, webinaires ni espace apprenant. */
const EDITOR_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [{ label: 'nav.home', path: '/dashboard', icon: Home }],
  },
  {
    key: 'creation',
    label: 'nav.section.creation',
    items: [
      { label: 'nav.eduflowList', path: '/eduflow/list', icon: Workflow },
      { label: 'nav.podcasts', path: '/podcasts', icon: Podcast },
      { label: 'nav.parcours', path: '/parcours', icon: Route },
    ],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [{ label: 'nav.help', path: '/help', icon: HelpCircle }],
  },
];

const NAV_CONFIG: Record<string, NavItem[]> = {
  editor: EDITOR_NAV_SECTIONS.flatMap((section) => section.items),
  institution: [
    { label: 'nav.institutionPanel', path: '/institution', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    {
      label: 'nav.webinarsEnterprise',
      path: '/webinars/enterprise',
      icon: Presentation,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
    },
    {
      label: 'nav.campaigns',
      path: '/institution?tab=rh-campaigns',
      icon: Megaphone,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_campaigns,
    },
    { label: 'nav.competencies', path: '/rh/competencies', icon: Target, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_competencies },
    { label: 'nav.requests', path: '/rh/requests', icon: ClipboardList, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_requests },
    { label: 'nav.catalog', path: '/rh/catalog', icon: BookOpen, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_catalog },
  ],
  institution_admin: [
    { label: 'nav.institutionPanel', path: '/institution', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    {
      label: 'nav.webinarsEnterprise',
      path: '/webinars/enterprise',
      icon: Presentation,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
    },
    {
      label: 'nav.campaigns',
      path: '/institution?tab=rh-campaigns',
      icon: Megaphone,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_campaigns,
    },
    { label: 'nav.competencies', path: '/rh/competencies', icon: Target, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_competencies },
    { label: 'nav.requests', path: '/rh/requests', icon: ClipboardList, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_requests },
    { label: 'nav.catalog', path: '/rh/catalog', icon: BookOpen, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_catalog },
  ],
  admin: [
    { label: 'nav.adminPanel', path: '/admin', icon: Shield },
  ],
  drh: [
    { label: 'nav.institutionPanel', path: '/institution', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    {
      label: 'nav.webinarsEnterprise',
      path: '/webinars/enterprise',
      icon: Presentation,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
    },
    {
      label: 'nav.campaigns',
      path: '/institution?tab=rh-campaigns',
      icon: Megaphone,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_campaigns,
    },
    {
      label: 'nav.codir',
      path: '/institution?tab=rh-module&rh=codir',
      icon: LayoutDashboard,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_codir,
    },
    {
      label: 'nav.competencies',
      path: '/institution?tab=rh-module&rh=competencies',
      icon: Target,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_competencies,
    },
  ],
  rh: [
    { label: 'nav.institutionPanel', path: '/institution', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    {
      label: 'nav.webinarsEnterprise',
      path: '/webinars/enterprise',
      icon: Presentation,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
    },
    {
      label: 'nav.campaigns',
      path: '/institution?tab=rh-campaigns',
      icon: Megaphone,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_campaigns,
    },
    {
      label: 'nav.competencies',
      path: '/institution?tab=rh-module&rh=competencies',
      icon: Target,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_competencies,
    },
    {
      label: 'nav.requests',
      path: '/institution?tab=rh-module&rh=requests',
      icon: ClipboardList,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_requests,
    },
    { label: 'nav.integrations', path: '/integrations', icon: Plug, institutionMenuFlag: INSTITUTION_MENU_FLAGS.integrations_page },
  ],
  resp_formation: [
    { label: 'nav.institutionPanel', path: '/institution', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    {
      label: 'nav.webinarsEnterprise',
      path: '/webinars/enterprise',
      icon: Presentation,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
    },
    {
      label: 'nav.campaigns',
      path: '/institution?tab=rh-campaigns',
      icon: Megaphone,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_campaigns,
    },
    {
      label: 'nav.competencies',
      path: '/institution?tab=rh-module&rh=competencies',
      icon: Target,
      institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_competencies,
    },
  ],
  manager: [
    { label: 'nav.dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'nav.myTeam', path: '/team', icon: Users, segmentFeature: SEGMENT_FEATURES.manager_space },
    { label: 'nav.requests', path: '/requests', icon: ClipboardList, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_requests },
    { label: 'nav.reviews', path: '/reviews', icon: Star, segmentFeature: SEGMENT_FEATURES.manager_space },
  ],
  collaborator: [
    { label: 'nav.myTraining', path: '/training', icon: GraduationCap, segmentFeature: SEGMENT_FEATURES.collaborator_space },
    { label: 'nav.mySkills', path: '/skills', icon: Target, segmentFeature: SEGMENT_FEATURES.collaborator_space },
    { label: 'nav.catalog', path: '/catalog', icon: BookOpen, institutionMenuFlag: INSTITUTION_MENU_FLAGS.rh_catalog },
    { label: 'nav.certificates', path: '/certificates', icon: Award },
  ],
  network: [
    { label: 'nav.networkGroup', path: '/network', icon: Building2 },
  ],
  learner: [
    { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
  ],
  student: [
    { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
  ],
};

function NavTooltip({ label, show }: { label: string; show: boolean }) {
  if (!show) return null;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg ltr:start-full ltr:ms-2 rtl:end-full rtl:me-2 dark:bg-gray-700"
    >
      {label}
    </div>
  );
}

function navPathMatchesLocation(path: string, loc: { pathname: string; search: string }): boolean {
  if (path.includes('?')) {
    const [p, q] = path.split('?', 2);
    if (loc.pathname !== p) return false;
    const want = new URLSearchParams(q);
    const have = new URLSearchParams(loc.search);
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  }
  return loc.pathname === path || loc.pathname.startsWith(`${path}/`);
}

export function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { sidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const { institutionLogoSrc, institutionLogoSmallSrc } = useInstitutionAppTheme();
  const location = useLocation();

  const role = (user?.role ?? 'teacher') as UserRole;
  const logoHomePath =
    role === 'network' ? '/network' : role === 'drh' || role === 'rh' || role === 'resp_formation' ? '/institution' : '/dashboard';
  const menuFlags = user?.institutionDashboardMenuFlags;

  const hasInstitution = !!user?.institutionId;
  const { isEducation: education, isAllowed: segmentAllows, context: segmentContext } = useInstitutionSegment();
  /** Drapeau de menu de l’établissement + disponibilité pour son segment (source unique `lib/institution-segment`). */
  const itemVisible = React.useCallback(
    (item: NavItem) =>
      (!item.institutionMenuFlag ||
        (isInstitutionMenuEnabled(menuFlags, item.institutionMenuFlag) && segmentAllows(item.institutionMenuFlag))) &&
      (!item.segmentFeature || segmentAllows(item.segmentFeature)) &&
      (!item.requiresInstitution || hasInstitution),
    [menuFlags, segmentAllows, hasInstitution],
  );

  const navItems = useMemo(() => {
    const rawItems = NAV_CONFIG[role] ?? TEACHER_NAV_ITEMS;
    let raw = Array.isArray(rawItems) && role === 'resp_formation' && education
      ? rawItems.filter((item) => !item.path.includes('rh-campaigns') && !item.path.includes('rh='))
      : rawItems;
    if (
      education &&
      Array.isArray(raw) &&
      (role === 'institution_admin' || role === 'institution' || role === 'resp_formation') &&
      !raw.some((item) => item.path === '/producer-assignments')
    ) {
      raw = [
        { label: 'nav.producerAssignments', path: '/producer-assignments', icon: Sparkles },
        ...raw,
      ];
    }
    if (!Array.isArray(raw)) return raw;
    return raw.filter(itemVisible);
  }, [role, education, itemVisible]);

  const navSections = useMemo(() => {
    if (roleQualifiesForEditorCreatorNav(role)) {
      return EDITOR_NAV_SECTIONS;
    }
    if (roleQualifiesForTrainerWebinarUi(role)) {
      return TEACHER_NAV_SECTIONS.map((section) => {
        if (section.key !== 'creation') {
          return {
            ...section,
            items: section.items.filter(itemVisible),
          };
        }
        const extra: NavItem[] = hasInstitution ? [{ label: 'nav.webinars', path: '/webinars', icon: Presentation }] : [];
        if (role === 'resp_formation') {
          extra.push({
            label: 'nav.webinarsEnterprise',
            path: '/webinars/enterprise',
            icon: Users,
            institutionMenuFlag: INSTITUTION_MENU_FLAGS.webinars_enterprise,
          });
        }
        const merged = [...section.items, ...extra];
        return {
          ...section,
          items: merged.filter(itemVisible),
        };
      });
    }
    return [{ key: 'default', label: '', items: navItems }];
  }, [role, navItems, hasInstitution, itemVisible]);

  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);

  return (
    <motion.div
      className="flex h-full flex-col overflow-hidden border-e border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] dark:border-[hsl(217,33%,22%)]"
      animate={{ width: sidebarCollapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {/* Logo */}
      <div className="flex h-16 flex-shrink-0 items-center border-b border-[color:var(--inst-player-border,#e5e7eb)] px-4 dark:border-[hsl(217,33%,22%)]">
        <NavLink
          to={logoHomePath}
          className={cn(
            'flex cursor-pointer items-center overflow-hidden',
            institutionLogoSrc
              ? cn('w-full px-0', sidebarCollapsed ? 'justify-center' : 'justify-start')
              : 'gap-2',
          )}
        >
            {institutionLogoSrc ? (
            <img
              src={sidebarCollapsed && institutionLogoSmallSrc ? institutionLogoSmallSrc : institutionLogoSrc}
              alt={t('sidebar.logoInstitutionAlt')}
              className={cn(
                'h-10 max-h-11 w-auto max-w-[min(100%,168px)] flex-shrink-0 object-contain',
                sidebarCollapsed ? 'h-8 max-h-8 max-w-[44px] object-center' : 'object-start',
              )}
            />
          ) : (
            <>
              <img src="/LogoSmall.png" alt={t('sidebar.logoAlt', 'LyrraStudio — Go to home')} className="h-8 w-8 flex-shrink-0 rounded-lg dark:hidden" />
              <img src="/LogoLightSmall.png" alt={t('sidebar.logoAlt', 'LyrraStudio — Go to home')} className="hidden h-8 w-8 flex-shrink-0 rounded-lg dark:block" />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="flex flex-col overflow-hidden leading-tight whitespace-nowrap"
                  >
                    <span className="text-lg font-bold text-slate-900 dark:text-white">LyrraStudio</span>
                    <span className="text-[10px] font-medium uppercase tracking-widest text-[color:var(--inst-player-text-muted,#64748b)] dark:text-[#B6CAEC]">
                      {t('sidebar.brandTagline')}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </NavLink>
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-3 scrollbar-thin"
        aria-label={t('nav.sidebarNavigation', 'Main navigation')}
        data-tour="tour-sidebar-nav"
      >
        {navSections.map((section) => (
          <div key={section.key} className="space-y-1">
            {!sidebarCollapsed && section.label && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--inst-player-text-muted,#64748b)] dark:text-[hsl(215,25%,72%)]">
                {t(section.label)}
              </p>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = navPathMatchesLocation(item.path, location);
              // Éducation : vocabulaire scolaire via les clés `<clé>_education` (repli sur la clé de base).
              const translatedLabel = t(item.label, { context: segmentContext });

              return (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => sidebarCollapsed && setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <NavLink
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_18%,transparent)] dark:text-white'
                        : 'text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F0F4FA] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0 transition-colors',
                        isActive ? 'text-[color:var(--inst-player-primary,#0C1C38)] dark:text-white' : '',
                      )}
                    />
                    <AnimatePresence>
                      {!sidebarCollapsed && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="truncate"
                        >
                          {translatedLabel}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active-indicator"
                        className="absolute bottom-2 start-0 top-2 w-1 rounded-e-full bg-[color:var(--inst-player-primary,#0C1C38)]"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </NavLink>
                  <NavTooltip label={translatedLabel as string} show={sidebarCollapsed && hoveredItem === item.path} />
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="flex-shrink-0 space-y-1 border-t border-[color:var(--inst-player-border,#e5e7eb)] p-2 dark:border-gray-700">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F0F4FA] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200 transition-colors"
          aria-label={sidebarCollapsed ? t('sidebar.expandAria') : t('sidebar.collapseAria')}
        >
          {sidebarCollapsed ? (
            <ChevronsRight className={cn('h-5 w-5 flex-shrink-0', rtlMirrorHorizontalChevron)} />
          ) : (
            <>
              <ChevronsLeft className={cn('h-5 w-5 flex-shrink-0', rtlMirrorHorizontalChevron)} />
              <span className="truncate">{t('sidebar.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

export default Sidebar;
